import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Loader, Modal, NotificationVariants, PillGroup } from '@base-app';
import { DateRangeDropdown, Section, SubPage, DateRangeSelection } from '@builder';
import { CollectionAndSequencingProps, Pill, PillId } from './CollectionAndSequencing.types';
import {
  RECEIVED_SAMPLES_STATUS_FILTER,
  SEQUENCED_SAMPLES_STATUS_FILTER,
  AWAITING_SAMPLES_STATUS_FILTER,
} from '@modules/study-insights/constants';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants/common';
import { LAYOUT_THREE, COMMON_TRANSLATION } from '@constants/index';
import {
  StyleDateContainer,
  StyledCollectionAndSequencingContainer,
  StyledDate,
  StyledLeftContainer,
  StyledRightContainer,
  StyledSelectContainer,
  StyledSelectedRange,
  StyledSubPageHeaderContainer,
} from './CollectionAndSequencing.styles';
import {
  clearTimeFromDate,
  getDateWithoutTZ,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getSelectedDateRangeString,
  getFormattedDate,
} from '@common/utils';
import {
  useMutationEditGoals,
  useQueryDemographics,
  useQueryEditGoals,
  useQuerySequencedSamples,
  useQuerySequencedSamplesOverview,
} from '@modules/study-insights/hooks';
import { CollectionType, DemographicsParam, DemographicsSerieStatus } from '../../types';
import { ScrollDirection } from '@common/types/ScrollDirection.type';
import { useGoalsStore, useSequenceSamplesStore } from '@modules/study-insights/store';
import { SequenceGoal } from './Overview/SequenceGoal/SequenceGoal.types';
import { useAddNotification } from '@modules/notifications/store';
import { Sizes } from '@common/types';
import {
  formatAwaitingSampleSeries,
  formatSequenceSampleSeries,
  formatReceivedSampleSeries,
} from '@modules/study-insights/utils';

import AwaitingSamples from './Samples/AwaitingSamples/AwaitingSamples';
import ConsentTrend from './Overview/ConsentTrend/ConsentTrend';
import Demographics from './Demographics/Demographics';
import EditContent from './Overview/EditContent/EditContent';
import FourYearSequenceGoal from './Overview/SequenceGoal/FourYearSequenceGoalCard/FourYearSequenceGoalCard';
import ReceivedSamples from './Samples/ReceivedSamples/ReceivedSamples';
import SequencedSamples from './Samples/SequencedSamples/SequencedSamples';
import useScrollDirection from '@common/hooks/useScrollDirection/useScrollDirection';
import YearlySequenceGoalCard from './Overview/SequenceGoal/YearlySequenceGoalCard/YearlySequenceGoalCard';

const CollectionAndSequencing: FC<CollectionAndSequencingProps> = () => {
  const { direction } = useScrollDirection();
  const { selectedDateRange, setSelectedDateRange, selectedPill, setSelectedPill } = useSequenceSamplesStore();
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);

  const addNotification = useAddNotification();
  const endDateGoal = useGoalsStore((store) => store.lifetimeGoal?.endDate);
  const setLifetimeGoal = useGoalsStore((store) => store.setLifetimeGoal);
  const startDateGoal = useGoalsStore((store) => store.lifetimeGoal?.startDate);

  const [endDate, setEndDate] = useState<Date | undefined>(getLastDayOfMonth());
  const [isNavSticky, setIsNavSticky] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [sectionTitle, setSectionTitle] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>(getFirstDayOfMonth());

  const DEMOGRAPHICS_STATUSES: Record<PillId, DemographicsSerieStatus[]> = {
    [PillId.SEQUENCED]: [
      DemographicsSerieStatus.RECOLLECT,
      DemographicsSerieStatus.IN_SEQUENCING,
      DemographicsSerieStatus.RESULTS_DELIVERED,
      DemographicsSerieStatus.SEQUENCING_COMPLETE,
    ],
    [PillId.RECEIVED]: [DemographicsSerieStatus.RECEIVED],
    [PillId.AWAITING]: [DemographicsSerieStatus.AWAITING],
  };

  const [demographicsStatus, setDemographicsStatus] = useState<DemographicsSerieStatus[]>(
    DEMOGRAPHICS_STATUSES[PillId.SEQUENCED],
  );

  const { data: awaitingSamplesData, isLoading: awaitingSamplesLoading } = useQuerySequencedSamples(
    {
      sampleStatus: AWAITING_SAMPLES_STATUS_FILTER,
    },
    {
      enabled: selectedPill === PillId.AWAITING,
    },
  );

  const { data: receivedSamplesData, isLoading: receivedSamplesLoading } = useQuerySequencedSamples(
    {
      sampleStatus: RECEIVED_SAMPLES_STATUS_FILTER,
      startDate: getFormattedDate(startDate, true),
      endDate: getFormattedDate(endDate, true),
    },
    {
      enabled: selectedPill === PillId.RECEIVED,
    },
  );

  const { data: sequencedSamplesData, isLoading: sequencedSamplesLoading } = useQuerySequencedSamples(
    {
      sampleStatus: SEQUENCED_SAMPLES_STATUS_FILTER,
      startDate: getFormattedDate(startDate, true),
      endDate: getFormattedDate(endDate, true),
    },
    {
      enabled: selectedPill === PillId.SEQUENCED,
    },
  );

  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isRefetching: isOverviewRefetching,
    refetch: refetchOverview,
  } = useQuerySequencedSamplesOverview(
    {
      endDate: clearTimeFromDate(new Date()),
    },
    {
      onSuccess: (data) => {
        setLifetimeGoal(data?.goals?.fourYear);
      },
    },
  );

  const getQueryDemographicsOptions = (): DemographicsParam => {
    if (demographicsStatus.includes(DemographicsSerieStatus.AWAITING)) {
      return {
        sampleStatus: demographicsStatus,
      };
    }

    return {
      startDate: getFormattedDate(startDate),
      endDate: getFormattedDate(endDate),
      sampleStatus: demographicsStatus,
    };
  };

  const { data: demographicsData, isLoading: isDemographicsLoading } = useQueryDemographics(
    getQueryDemographicsOptions(),
  );

  const { data: editGoalsData, isRefetching: isRefetchingGoals } = useQueryEditGoals();

  const handleEditGoalsMutationSuccess = async (): Promise<any> => {
    addNotification({
      autoClose: true,
      title: t('yearly_sequence_goal.success_message'),
      type: NotificationVariants.SUCCESS,
    });
    // eslint-disable-next-line  @typescript-eslint/no-floating-promises
    refetchOverview();
  };

  const handleEditGoalsMutationError = (): void => {
    addNotification({
      autoClose: true,
      title: t('yearly_sequence_goal.error_message'),
      type: NotificationVariants.ERROR,
    });
    setIsOpenModal(false);
  };

  const { mutateAsync: mutateGoals } = useMutationEditGoals(
    // eslint-disable-next-line  @typescript-eslint/no-misused-promises
    handleEditGoalsMutationSuccess,
    // eslint-disable-next-line  @typescript-eslint/no-misused-promises
    handleEditGoalsMutationError,
  );

  const PILL_LIST: Pill[] = [
    {
      label: t('sequenced_samples.title'),
      id: PillId.SEQUENCED,
    },
    {
      label: t('received_samples.title'),
      id: PillId.RECEIVED,
    },
    {
      label: t('awaiting_samples'),
      id: PillId.AWAITING,
    },
  ];

  const handleOnClickPill = (id: string): void => {
    setSelectedPill(id as PillId);
  };

  const handleSelectDateRange = (selection: DateRangeSelection): void => {
    setStartDate(selection.startDate);
    setEndDate(selection.endDate);
    setSelectedDateRange(selection);
  };

  const getSelectedData = (): string => {
    if (startDate === undefined || endDate === undefined) {
      return t('date_range_dropdown_options.all_time', { ns: COMMON_TRANSLATION });
    }

    return getSelectedDateRangeString(startDate, endDate);
  };

  const renderSubPageHeader = (): ReactNode => {
    return selectedPill ? (
      <StyledSubPageHeaderContainer
        isSticky={isNavSticky}
        css={{
          top: direction === ScrollDirection.DOWN ? 0 : '$stickyNav$height',
        }}
      >
        <Box
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            maxWidth: '$studyInsights$headerMaxWidth',
            width: '100%',
          }}
        >
          <PillGroup items={PILL_LIST} selectedPill={selectedPill} onClickPill={handleOnClickPill} />
          {(selectedPill === PillId.SEQUENCED || selectedPill === PillId.RECEIVED) && (
            <StyleDateContainer>
              <StyledSelectedRange data-testid="selected-date-range">
                {t('selected_date_range.label')}
                <StyledDate> {getSelectedData()} </StyledDate>
              </StyledSelectedRange>
              <StyledSelectContainer>
                <DateRangeDropdown
                  dateRangeErrorTitle={t('selected_date_range.error.title')}
                  dateRangeErrorDescription={t('selected_date_range.error.description', {
                    dateRange:
                      startDateGoal &&
                      endDateGoal &&
                      getSelectedDateRangeString(
                        new Date(getDateWithoutTZ(startDateGoal)),
                        new Date(getDateWithoutTZ(endDateGoal)),
                      ),
                  })}
                  maxDate={endDateGoal ? new Date(getDateWithoutTZ(endDateGoal)) : undefined}
                  minDate={startDateGoal ? new Date(getDateWithoutTZ(startDateGoal)) : undefined}
                  onSelect={handleSelectDateRange}
                  layout={LAYOUT_THREE}
                />
              </StyledSelectContainer>
            </StyleDateContainer>
          )}
        </Box>
      </StyledSubPageHeaderContainer>
    ) : null;
  };

  const handleScroll = (): void => {
    const container = document.getElementById('sequencing-data-section');

    const containerPosition = container?.getBoundingClientRect().top ?? 0;
    const isSecondNavSticky = containerPosition < 0;

    setIsNavSticky(isSecondNavSticky);
  };

  const handleEdit = (): void => {
    setIsOpenModal(true);
  };

  const handleCloseModal = (): void => {
    setIsOpenModal(false);
  };

  const onSubmit = async (editedData: SequenceGoal[]): Promise<any> => {
    setIsOpenModal(false);
    await mutateGoals({ goals: editedData });
  };

  useEffect(() => {
    PILL_LIST.forEach((pill) => {
      if (pill.id === selectedPill) {
        let title = '';

        if (selectedPill === PillId.SEQUENCED) {
          title = t('sequencing_data');
        }

        if (selectedPill === PillId.RECEIVED) {
          title = t('received_samples_data');
        }

        if (selectedPill === PillId.AWAITING) {
          title = t('awaiting_samples');
        }

        return setSectionTitle(title);
      }
    });
    setDemographicsStatus(DEMOGRAPHICS_STATUSES[selectedPill as PillId]);
  }, [selectedPill]);

  useEffect(() => {
    setSelectedPill(PillId.SEQUENCED);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <SubPage hasSeparator={true}>
        <Section
          title={isOverviewLoading || isOverviewRefetching ? <Loader css={{ width: '400px' }} /> : t('overview')}
        >
          <StyledCollectionAndSequencingContainer>
            {(isOverviewLoading || isOverviewRefetching) && <Loader css={{ height: '520px' }} />}
            {!isOverviewRefetching && !isOverviewLoading && !!overviewData && (
              <>
                <StyledLeftContainer>
                  <ConsentTrend data={overviewData} handleEdit={handleEdit} />
                </StyledLeftContainer>
                <StyledRightContainer>
                  <YearlySequenceGoalCard data={overviewData.goals?.yearly} handleEdit={handleEdit} />
                  <FourYearSequenceGoal data={overviewData.goals?.fourYear} handleEdit={handleEdit} />
                </StyledRightContainer>
              </>
            )}
          </StyledCollectionAndSequencingContainer>
        </Section>
      </SubPage>
      <SubPage header={renderSubPageHeader()} id="sequencing-data-section">
        <Section title={sectionTitle}>
          {selectedPill === PillId.SEQUENCED && (
            <>
              {sequencedSamplesLoading && <Loader css={{ height: '520px' }} />}
              {!sequencedSamplesLoading && sequencedSamplesData && !!selectedDateRange?.selectedDateRange && (
                <SequencedSamples
                  data={formatSequenceSampleSeries(sequencedSamplesData?.series) as CollectionType}
                  period={selectedDateRange.selectedDateRange}
                  stats={sequencedSamplesData.stats}
                />
              )}
            </>
          )}
          {selectedPill === PillId.RECEIVED && (
            <>
              {receivedSamplesLoading && <Loader css={{ height: '520px' }} />}
              {!receivedSamplesLoading && receivedSamplesData && !!selectedDateRange?.selectedDateRange && (
                <ReceivedSamples
                  data={
                    formatReceivedSampleSeries(receivedSamplesData?.series, {
                      includeStatus: false,
                      includeType: true,
                    }) as CollectionType
                  }
                  period={selectedDateRange.selectedDateRange}
                  stats={receivedSamplesData.stats}
                />
              )}
            </>
          )}
          {selectedPill === PillId.AWAITING && (
            <>
              {awaitingSamplesLoading && <Loader />}
              {!awaitingSamplesLoading && awaitingSamplesData?.series && (
                <AwaitingSamples
                  data={
                    formatAwaitingSampleSeries(awaitingSamplesData?.series, {
                      includeStatus: false,
                      includeType: false,
                    }) as CollectionType
                  }
                />
              )}
            </>
          )}
        </Section>
      </SubPage>
      <SubPage>
        <Section title={isDemographicsLoading ? <Loader css={{ width: '400px' }} /> : t('demographics.title')}>
          <>
            {isDemographicsLoading && <Loader css={{ height: '520px' }} />}
            {!isDemographicsLoading && demographicsData && selectedPill && (
              <Demographics data={demographicsData} selectedPillId={selectedPill} />
            )}
          </>
        </Section>
      </SubPage>
      <Modal
        css={{ overflow: 'hidden' }}
        onClose={handleCloseModal}
        show={isOpenModal}
        size={Sizes.EXTRA_LARGE}
        title={t('yearly_sequence_goal.goal_edit')}
      >
        <>
          {isRefetchingGoals && <Loader />}
          {!isRefetchingGoals && editGoalsData !== undefined && (
            <EditContent data={editGoalsData} onSubmit={onSubmit} />
          )}
        </>
      </Modal>
    </>
  );
};

export default CollectionAndSequencing;
